import { Crisp as CrispSDK } from 'crisp-sdk-web'
import { z } from 'zod'
import manifest from '@tunasong/manifest'

const env = z
  .object({
    VITE_CRISP_WEBSITE_ID: z.string().optional(),
  })
  .parse(import.meta.env)

/** Crisp website id for this build; unset in builds without Crisp, or when the manifest turns support chat off */
export const crispWebsiteId = manifest.platformFlags.supportChat ? env.VITE_CRISP_WEBSITE_ID : undefined

/** Support chat is part of this build. False hides every entry point to it, rather than showing it as unavailable. */
export const isSupportChatEnabled = () => Boolean(crispWebsiteId)

/** Crisp swaps its `$crisp` command queue (an array) for an API object with `is()` once it has booted */
const isCrispBooted = () => typeof (globalThis as { $crisp?: { is?: unknown } }).$crisp?.is === 'function'

/** The support chat can actually open: configured, and not blocked or still loading */
export const isSupportChatAvailable = () => isSupportChatEnabled() && isCrispBooted()

/**
 * Open hide requests. Any surface may ask for the bubble to be hidden (entity pages, Performance);
 * it is shown again only once every request is released, so leaving a nested hider (Performance
 * inside an entity page) does not bring the bubble back.
 */
let hideRequests = 0

export const syncCrispVisibility = () => {
  if (!isSupportChatEnabled()) {
    return
  }
  if (hideRequests > 0) {
    CrispSDK.chat.hide()
    return
  }
  CrispSDK.chat.show()
}

/** Hide the bubble until the returned release is called */
export const requestCrispHidden = () => {
  hideRequests += 1
  syncCrispVisibility()
  let released = false
  return () => {
    if (released) {
      return
    }
    released = true
    hideRequests -= 1
    syncCrispVisibility()
  }
}

/**
 * Open the support chat, even where the bubble is hidden. When the user closes the chat the
 * current hide state applies again. Returns false when there is no chat to open.
 */
export const openSupportChat = () => {
  if (!isSupportChatAvailable()) {
    return false
  }
  CrispSDK.chat.show()
  CrispSDK.chat.open()
  CrispSDK.chat.onChatClosed(syncCrispVisibility)
  return true
}
